import { useState, useEffect } from 'react';
import axios from 'axios';
import { Header } from '../../components/Header';
import { BottomNav } from '../../components/BottomNav';
import { MapPin, Check, AlertCircle, Plus, Phone, Pencil, Trash2, X, Receipt } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminPuntos() {
  const [puntosVenta, setPuntosVenta] = useState([]);
  const [emisores, setEmisores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create
  const [showCreate, setShowCreate] = useState(false);
  const [newPunto, setNewPunto] = useState({ name: '', address: '', telefono: '', categoria: 'local', codigo_postal: '', facturacion_habilitada: false, serie_facturacion: '', emisor_id: '' });
  const [creating, setCreating] = useState(false);

  // Edit
  const [editingPunto, setEditingPunto] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Delete
  const [deletingId, setDeletingId] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(null);

  useEffect(() => { loadPuntosVenta(); loadEmisores(); }, []);

  const loadPuntosVenta = async () => {
    try {
      const { data } = await axios.get(`${API}/puntos-venta`);
      setPuntosVenta(data.sort((a, b) => a.name.localeCompare(b.name, 'es')));
    } catch {
      setError('Error al cargar puntos de venta');
    } finally {
      setLoading(false);
    }
  };

  const loadEmisores = async () => {
    try {
      const { data } = await axios.get(`${API}/emisores`);
      setEmisores(data);
    } catch {
      // Si falla, los selects de emisor simplemente quedan vacíos.
    }
  };

  const handleCreate = async () => {
    if (!newPunto.name) { setError('Ingresa el nombre del punto de venta'); return; }
    if (newPunto.facturacion_habilitada && newPunto.codigo_postal.length !== 5) {
      setError('Ingresa un código postal válido (5 dígitos) para habilitar facturación');
      return;
    }
    if (newPunto.facturacion_habilitada && !newPunto.emisor_id) {
      setError('Selecciona el emisor (RFC) para habilitar facturación');
      return;
    }
    setError('');
    setCreating(true);
    try {
      await axios.post(`${API}/puntos-venta`, newPunto);
      setShowCreate(false);
      setNewPunto({ name: '', address: '', telefono: '', categoria: 'local', codigo_postal: '', facturacion_habilitada: false, serie_facturacion: '', emisor_id: '' });
      await loadPuntosVenta();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear punto de venta');
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (punto) => {
    setEditingPunto(punto);
    setEditForm({
      name: punto.name,
      address: punto.address || '',
      telefono: punto.telefono || '',
      categoria: punto.categoria || 'local',
      codigo_postal: punto.codigo_postal || '',
      facturacion_habilitada: punto.facturacion_habilitada || false,
      serie_facturacion: punto.serie_facturacion || '',
      emisor_id: punto.emisor_id || '',
    });
    setError('');
  };

  const handleSaveEdit = async () => {
    if (!editForm.name) { setError('El nombre es requerido'); return; }
    if (editForm.facturacion_habilitada && editForm.codigo_postal.length !== 5) {
      setError('Ingresa un código postal válido (5 dígitos) para habilitar facturación');
      return;
    }
    if (editForm.facturacion_habilitada && !editForm.emisor_id) {
      setError('Selecciona el emisor (RFC) para habilitar facturación');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await axios.put(`${API}/puntos-venta/${editingPunto.id}`, editForm);
      setEditingPunto(null);
      await loadPuntosVenta();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar cambios');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (puntoId) => {
    setDeletingId(puntoId);
    try {
      await axios.delete(`${API}/puntos-venta/${puntoId}`);
      await loadPuntosVenta();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al eliminar punto de venta');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-puntos-page">
      <Header title="Puntos de Venta" />

      <div className="page-container fade-in">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <p className="text-red-700 font-medium">{error}</p>
          </div>
        )}

        <button
          onClick={() => { setShowCreate(true); setError(''); }}
          className="w-full h-16 bg-blue-600 text-white rounded-xl text-xl font-bold btn-press flex items-center justify-center gap-3 mb-6"
          data-testid="btn-add-punto"
        >
          <Plus className="w-6 h-6" />
          Agregar Punto de Venta
        </button>

        <h3 className="text-lg font-bold text-zinc-900 mb-4 flex items-center gap-2">
          <MapPin className="w-5 h-5" />
          Puntos de Venta ({puntosVenta.length})
        </h3>

        <div className="space-y-3">
          {puntosVenta.map((punto) => (
            <div key={punto.id} className="bg-white border-2 border-zinc-200 rounded-xl p-4" data-testid={`punto-${punto.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-lg leading-tight">{punto.name}</p>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      punto.categoria === 'moto' ? 'bg-green-100 text-green-700' :
                      punto.categoria === 'puesto' ? 'bg-amber-100 text-amber-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {punto.categoria === 'moto' ? 'Moto' : punto.categoria === 'puesto' ? 'Puesto' : 'Local físico'}
                    </span>
                    {punto.facturacion_habilitada && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 flex items-center gap-1">
                        <Receipt className="w-3 h-3" />Factura
                      </span>
                    )}
                  </div>
                  {punto.address && <p className="text-zinc-500 text-sm">{punto.address}</p>}
                  {punto.facturacion_habilitada && punto.codigo_postal && (
                    <p className="text-zinc-400 text-xs mt-1">
                      CP {punto.codigo_postal}{punto.serie_facturacion ? ` · Serie ${punto.serie_facturacion}` : ''}
                      {punto.emisor_id ? ` · ${emisores.find(em => em.id === punto.emisor_id)?.rfc || 'Emisor eliminado'}` : ' · Sin emisor asignado'}
                    </p>
                  )}
                  {punto.telefono && (
                    <p className="flex items-center gap-1 text-zinc-400 text-xs mt-1">
                      <Phone className="w-3 h-3" />{punto.telefono}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => openEdit(punto)}
                    className="w-9 h-9 flex items-center justify-center bg-zinc-100 rounded-lg"
                    data-testid={`btn-edit-punto-${punto.id}`}
                  >
                    <Pencil className="w-4 h-4 text-zinc-600" />
                  </button>
                  <button
                    onClick={() => setConfirmingDelete(punto.id)}
                    className="w-9 h-9 flex items-center justify-center bg-red-50 rounded-lg"
                    data-testid={`btn-delete-punto-${punto.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>
              {/* Delete confirmation */}
              {confirmingDelete === punto.id && (
                <div className="mt-3 pt-3 border-t border-red-100 flex items-center justify-between gap-3">
                  <span className="text-sm text-red-700 font-medium">¿Eliminar {punto.name}?</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setConfirmingDelete(null)}
                      className="px-3 h-8 bg-zinc-100 rounded-lg text-sm font-medium"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={() => { setConfirmingDelete(null); handleDelete(punto.id); }}
                      disabled={deletingId === punto.id}
                      className="px-3 h-8 bg-red-600 text-white rounded-lg text-sm font-medium disabled:opacity-40 flex items-center gap-1"
                      data-testid={`btn-confirm-delete-punto-${punto.id}`}
                    >
                      {deletingId === punto.id
                        ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        : null}
                      Sí, eliminar
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Create Modal */}
        {showCreate && (
          <div className="modal-overlay" onClick={() => setShowCreate(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <h2 className="text-2xl font-bold mb-6">Nuevo Punto de Venta</h2>
              <div className="space-y-4 mb-6">
                <input type="text" value={newPunto.name}
                  onChange={e => setNewPunto({ ...newPunto, name: e.target.value })}
                  placeholder="Nombre del punto *"
                  className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl"
                  data-testid="input-punto-name"
                />
                <input type="text" value={newPunto.address}
                  onChange={e => setNewPunto({ ...newPunto, address: e.target.value })}
                  placeholder="Dirección (opcional)"
                  className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl"
                  data-testid="input-punto-address"
                />
                <input type="tel" value={newPunto.telefono}
                  onChange={e => setNewPunto({ ...newPunto, telefono: e.target.value })}
                  placeholder="Teléfono WhatsApp (ej: 5512345678)"
                  className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl"
                  data-testid="input-punto-telefono"
                />
                <select
                  value={newPunto.categoria}
                  onChange={e => setNewPunto({ ...newPunto, categoria: e.target.value })}
                  className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl bg-white"
                  data-testid="input-punto-categoria"
                >
                  <option value="local">Local físico</option>
                  <option value="puesto">Puesto</option>
                  <option value="moto">Moto</option>
                </select>

                <div className="pt-2 border-t border-zinc-100">
                  <div className="flex items-center justify-between py-2">
                    <div>
                      <p className="font-medium text-zinc-800 text-sm flex items-center gap-1.5"><Receipt className="w-4 h-4" />Habilitar facturación</p>
                      <p className="text-xs text-zinc-500">Permite generar facturas de este punto de venta</p>
                    </div>
                    <button type="button"
                      onClick={() => setNewPunto({ ...newPunto, facturacion_habilitada: !newPunto.facturacion_habilitada })}
                      className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${newPunto.facturacion_habilitada ? 'bg-purple-600' : 'bg-zinc-300'}`}
                      data-testid="toggle-punto-facturacion"
                    >
                      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${newPunto.facturacion_habilitada ? 'left-6' : 'left-0.5'}`} />
                    </button>
                  </div>
                  {newPunto.facturacion_habilitada && (
                    <div className="space-y-3 mt-2">
                      <select
                        value={newPunto.emisor_id}
                        onChange={e => setNewPunto({ ...newPunto, emisor_id: e.target.value })}
                        className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl bg-white"
                        data-testid="input-punto-emisor"
                      >
                        <option value="">Selecciona el emisor (RFC) *</option>
                        {emisores.map(em => (
                          <option key={em.id} value={em.id}>{em.rfc} — {em.razon_social}</option>
                        ))}
                      </select>
                      <input type="text" value={newPunto.codigo_postal}
                        onChange={e => setNewPunto({ ...newPunto, codigo_postal: e.target.value.replace(/\D/g, '').slice(0, 5) })}
                        placeholder="Código postal (5 dígitos) *"
                        className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl"
                        data-testid="input-punto-cp"
                      />
                      <input type="text" value={newPunto.serie_facturacion}
                        onChange={e => setNewPunto({ ...newPunto, serie_facturacion: e.target.value.toUpperCase().slice(0, 10) })}
                        placeholder="Serie de folio (opcional, ej: MER)"
                        className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl"
                        data-testid="input-punto-serie"
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setShowCreate(false)} className="h-14 bg-zinc-100 rounded-xl font-bold btn-press">
                  Cancelar
                </button>
                <button onClick={handleCreate} disabled={creating}
                  className="h-14 bg-blue-600 text-white rounded-xl font-bold btn-press flex items-center justify-center gap-2"
                  data-testid="btn-create-punto"
                >
                  {creating ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : <Check className="w-5 h-5" />}
                  Crear
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {editingPunto && (
          <div className="modal-overlay" onClick={() => setEditingPunto(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Editar Punto de Venta</h2>
                <button onClick={() => setEditingPunto(null)} className="w-9 h-9 flex items-center justify-center bg-zinc-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />{error}
                </div>
              )}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-zinc-500 mb-1">Nombre *</label>
                  <input type="text" value={editForm.name}
                    onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl"
                    data-testid="input-edit-punto-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-500 mb-1">Dirección</label>
                  <input type="text" value={editForm.address}
                    onChange={e => setEditForm({ ...editForm, address: e.target.value })}
                    placeholder="Opcional"
                    className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl"
                    data-testid="input-edit-punto-address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-500 mb-1">Teléfono WhatsApp</label>
                  <input type="tel" value={editForm.telefono}
                    onChange={e => setEditForm({ ...editForm, telefono: e.target.value })}
                    placeholder="Ej: 5512345678"
                    className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl"
                    data-testid="input-edit-punto-telefono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-500 mb-1">Categoría</label>
                  <select
                    value={editForm.categoria || 'local'}
                    onChange={e => setEditForm({ ...editForm, categoria: e.target.value })}
                    className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl bg-white"
                    data-testid="input-edit-punto-categoria"
                  >
                    <option value="local">Local físico</option>
                    <option value="puesto">Puesto</option>
                    <option value="moto">Moto</option>
                  </select>
                </div>
                <div className="pt-2 border-t border-zinc-100">
                  <div className="flex items-center justify-between py-2">
                    <div>
                      <p className="font-medium text-zinc-800 text-sm flex items-center gap-1.5"><Receipt className="w-4 h-4" />Habilitar facturación</p>
                      <p className="text-xs text-zinc-500">Permite generar facturas de este punto de venta</p>
                    </div>
                    <button type="button"
                      onClick={() => setEditForm({ ...editForm, facturacion_habilitada: !editForm.facturacion_habilitada })}
                      className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${editForm.facturacion_habilitada ? 'bg-purple-600' : 'bg-zinc-300'}`}
                      data-testid="toggle-edit-punto-facturacion"
                    >
                      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${editForm.facturacion_habilitada ? 'left-6' : 'left-0.5'}`} />
                    </button>
                  </div>
                  {editForm.facturacion_habilitada && (
                    <div className="space-y-3 mt-2">
                      <div>
                        <label className="block text-sm font-medium text-zinc-500 mb-1">Emisor (RFC) *</label>
                        <select
                          value={editForm.emisor_id}
                          onChange={e => setEditForm({ ...editForm, emisor_id: e.target.value })}
                          className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl bg-white"
                          data-testid="input-edit-punto-emisor"
                        >
                          <option value="">Selecciona un emisor</option>
                          {emisores.map(em => (
                            <option key={em.id} value={em.id}>{em.rfc} — {em.razon_social}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-zinc-500 mb-1">Código postal *</label>
                        <input type="text" value={editForm.codigo_postal}
                          onChange={e => setEditForm({ ...editForm, codigo_postal: e.target.value.replace(/\D/g, '').slice(0, 5) })}
                          placeholder="5 dígitos"
                          className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl"
                          data-testid="input-edit-punto-cp"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-zinc-500 mb-1">Serie de folio</label>
                        <input type="text" value={editForm.serie_facturacion}
                          onChange={e => setEditForm({ ...editForm, serie_facturacion: e.target.value.toUpperCase().slice(0, 10) })}
                          placeholder="Opcional, ej: MER"
                          className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl"
                          data-testid="input-edit-punto-serie"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setEditingPunto(null)} className="h-14 bg-zinc-100 rounded-xl font-bold btn-press">
                  Cancelar
                </button>
                <button onClick={handleSaveEdit} disabled={saving}
                  className="h-14 bg-zinc-900 text-white rounded-xl font-bold btn-press flex items-center justify-center gap-2"
                  data-testid="btn-save-punto"
                >
                  {saving ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : <Check className="w-5 h-5" />}
                  Guardar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
